import React, { useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/layout/Layout';
import { ExportButtons } from '../components/reports/ExportButtons';
import { useMonthlySummary, useYearlySummary, useYearlyDrivers, useYearlyVehicles } from '../hooks/useReports';
import { formatNumber, formatPercentage } from '../utils/formatters';
import { YearlyDriverRow, YearlyVehicleRow } from '../types/report';

// ─── Stacked bar showing pro / perso / pending proportions ───────────────────
const KmBar: React.FC<{ proKm: number; persoKm: number; pendingKm: number; totalKm: number }> = ({
  proKm,
  persoKm,
  pendingKm,
  totalKm,
}) => {
  if (totalKm === 0) return null;
  const proPct = (proKm / totalKm) * 100;
  const persoPct = (persoKm / totalKm) * 100;
  const pendingPct = (pendingKm / totalKm) * 100;

  return (
    <div className="flex h-2 rounded-full overflow-hidden w-full min-w-[80px]">
      <div className="bg-green-500" style={{ width: `${proPct}%` }} />
      <div className="bg-blue-400" style={{ width: `${persoPct}%` }} />
      <div className="bg-amber-400" style={{ width: `${pendingPct}%` }} />
    </div>
  );
};

// ─── Percentage badge ─────────────────────────────────────────────────────────
const PctBadge: React.FC<{ value: number; color: 'green' | 'blue' | 'amber' }> = ({
  value,
  color,
}) => {
  const cls = {
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
  }[color];

  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {value}%
    </span>
  );
};

// ─── Format km with space as thousand separator ───────────────────────────────
const fmtKm = (n: number): string =>
  new Intl.NumberFormat('fr-FR').format(Math.round(n));

// ─── Skeleton loader ──────────────────────────────────────────────────────────
const YearlySkeleton: React.FC = () => (
  <div className="animate-pulse space-y-4">
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-28 bg-gray-200 rounded-lg" />
      ))}
    </div>
    <div className="h-64 bg-gray-200 rounded-lg" />
  </div>
);

// ─── Month keys for i18n ──────────────────────────────────────────────────────
const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const YEARS = [2024, 2025, 2026];

type Period = 'monthly' | 'yearly';

export const ReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  // ─── Unified filter state ─────────────────────────────────────────────────
  const [period, setPeriod] = useState<Period>('monthly');
  const [selectedMonth, setSelectedMonth] = useState<string>(format(currentDate, 'yyyy-MM'));
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  // ─── Sub-state: driver/vehicle tab inside yearly view ─────────────────────
  const [activeTab, setActiveTab] = useState<'drivers' | 'vehicles'>('drivers');

  // ─── Derived month/year from selectedMonth string ─────────────────────────
  const [monthYear, monthNum] = selectedMonth.split('-').map(Number);

  // ─── Data hooks ───────────────────────────────────────────────────────────
  const { data: monthlySummary, isLoading: monthlyLoading } = useMonthlySummary(selectedMonth);
  const { data: yearlySummary, isLoading: yearlySummaryLoading } = useYearlySummary(selectedYear);
  const { data: driversData, isLoading: driversLoading } = useYearlyDrivers(selectedYear);
  const { data: vehiclesData, isLoading: vehiclesLoading } = useYearlyVehicles(selectedYear);

  const yearlyLoading = yearlySummaryLoading || driversLoading || vehiclesLoading;

  const availableYears = yearlySummary?.available_years ?? YEARS;

  const drivers: YearlyDriverRow[] = driversData?.drivers
    ? [...driversData.drivers].sort((a, b) => b.total_km - a.total_km)
    : [];

  const vehicles: YearlyVehicleRow[] = vehiclesData?.vehicles
    ? [...vehiclesData.vehicles].sort((a, b) => b.total_km - a.total_km)
    : [];

  const yearlyHasNoData =
    !yearlyLoading &&
    yearlySummary !== null &&
    yearlySummary !== undefined &&
    yearlySummary.total_km === 0;

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleMonthChange = (newMonth: number) => {
    setSelectedMonth(`${monthYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const handleMonthYearChange = (newYear: number) => {
    setSelectedMonth(`${newYear}-${String(monthNum).padStart(2, '0')}`);
  };

  return (
    <Layout>
      <div className="space-y-6">

        {/* ── Page header ───────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-900">{t('reports.title')}</h1>
          {period === 'monthly' && (
            <ExportButtons filters={{ type: 'monthly', month: selectedMonth, year: monthYear }} />
          )}
          {period === 'yearly' && (
            <ExportButtons filters={{ type: 'annual', year: selectedYear }} />
          )}
        </div>

        {/* ── Filter bar ────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">

            {/* Period segmented control */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm font-medium shrink-0">
              <button
                onClick={() => setPeriod('monthly')}
                className={`px-4 py-2 transition-colors ${
                  period === 'monthly'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t('reports.period.monthly', 'Mensuel')}
              </button>
              <button
                onClick={() => setPeriod('yearly')}
                className={`px-4 py-2 border-l border-gray-300 transition-colors ${
                  period === 'yearly'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t('reports.period.yearly', 'Annuel')}
              </button>
            </div>

            {/* Separator */}
            <div className="hidden sm:block w-px h-6 bg-gray-200" />

            {/* Month picker — only shown for Mensuel */}
            {period === 'monthly' && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600 whitespace-nowrap">
                  {t('reports.month')}
                </label>
                <select
                  value={monthNum}
                  onChange={(e) => handleMonthChange(Number(e.target.value))}
                  className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  {MONTH_KEYS.map((key, i) => (
                    <option key={i} value={i + 1}>
                      {t(`reports.months.${key}`)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Year picker */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600 whitespace-nowrap">
                {t('reports.year')}
              </label>
              {period === 'monthly' ? (
                <select
                  value={monthYear}
                  onChange={(e) => handleMonthYearChange(Number(e.target.value))}
                  className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  {YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  {availableYears.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              )}
            </div>

          </div>
        </div>

        {/* ── Monthly content ───────────────────────────────────────────────── */}
        {period === 'monthly' && (
          <>
            {monthlyLoading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-32 bg-gray-200 rounded-lg" />
                <div className="h-64 bg-gray-200 rounded-lg" />
              </div>
            ) : monthlySummary ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold mb-6">
                  {t('reports.summary', {
                    month: t(`reports.months.${MONTH_KEYS[monthNum - 1]}`),
                    year: monthYear,
                  })}
                </h2>

                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">{t('reports.totalDistance')}</div>
                    <div className="text-3xl font-bold text-gray-900">
                      {formatNumber(monthlySummary.total_km)} km
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-professional-600">
                          {t('reports.professional')}
                        </span>
                        <span className="text-gray-700">
                          {formatNumber(monthlySummary.professional_km)} km (
                          {formatPercentage(
                            monthlySummary.professional_km,
                            monthlySummary.total_km
                          )}
                          )
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-professional-500 h-2 rounded-full"
                          style={{
                            width: `${
                              (monthlySummary.professional_km / monthlySummary.total_km) * 100
                            }%`,
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-personal-600">
                          {t('reports.personal')}
                        </span>
                        <span className="text-gray-700">
                          {formatNumber(monthlySummary.personal_km)} km (
                          {formatPercentage(monthlySummary.personal_km, monthlySummary.total_km)}
                          )
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-personal-500 h-2 rounded-full"
                          style={{
                            width: `${
                              (monthlySummary.personal_km / monthlySummary.total_km) * 100
                            }%`,
                          }}
                        />
                      </div>
                    </div>

                    {monthlySummary.pending_km > 0 && (
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-600">
                            {t('reports.pending')}
                          </span>
                          <span className="text-gray-700">
                            {formatNumber(monthlySummary.pending_km)} km (
                            {formatPercentage(monthlySummary.pending_km, monthlySummary.total_km)}
                            )
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gray-400 h-2 rounded-full"
                            style={{
                              width: `${
                                (monthlySummary.pending_km / monthlySummary.total_km) * 100
                              }%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-gray-200 grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600">{t('reports.totalTrips')}</div>
                      <div className="text-xl font-semibold text-gray-900">
                        {monthlySummary.total_trips}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">{t('reports.qualificationRate')}</div>
                      <div className="text-xl font-semibold text-gray-900">
                        {formatPercentage(
                          monthlySummary.professional_count + monthlySummary.personal_count,
                          monthlySummary.total_trips
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">{t('reports.pending')}</div>
                      <div className="text-xl font-semibold text-gray-900">
                        {monthlySummary.pending_count}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">{t('reports.noData')}</h3>
                <p className="text-gray-500 mt-2">
                  {t('reports.noTripsForMonth', {
                    month: t(`reports.months.${MONTH_KEYS[monthNum - 1]}`),
                    year: monthYear,
                  })}
                </p>
              </div>
            )}
          </>
        )}

        {/* ── Yearly content ────────────────────────────────────────────────── */}
        {period === 'yearly' && (
          <>
            {yearlyLoading ? (
              <YearlySkeleton />
            ) : yearlyHasNoData ? (
              <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="text-5xl mb-4">📊</div>
                <h3 className="text-lg font-medium text-gray-900">
                  {t('reports.yearly.noData', { year: selectedYear })}
                </h3>
                <p className="text-gray-500 mt-2 text-sm">{t('reports.yearly.noDataMessage')}</p>
              </div>
            ) : yearlySummary ? (
              <>
                {/* KPI cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      {t('reports.yearly.totalKm')}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {fmtKm(yearlySummary.total_km)}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">km</div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      {t('reports.yearly.proKm')}
                    </div>
                    <div className="text-2xl font-bold text-green-600">
                      {fmtKm(yearlySummary.professional_km)}
                    </div>
                    <div className="mt-1">
                      <PctBadge value={yearlySummary.professional_pct} color="green" />
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      {t('reports.yearly.persoKm')}
                    </div>
                    <div className="text-2xl font-bold text-blue-600">
                      {fmtKm(yearlySummary.personal_km)}
                    </div>
                    <div className="mt-1">
                      <PctBadge value={yearlySummary.personal_pct} color="blue" />
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      {t('reports.yearly.pendingKm')}
                    </div>
                    <div className="text-2xl font-bold text-amber-600">
                      {fmtKm(yearlySummary.pending_km)}
                    </div>
                    <div className="mt-1">
                      <PctBadge
                        value={Math.round(
                          yearlySummary.total_km > 0
                            ? (yearlySummary.pending_km / yearlySummary.total_km) * 100
                            : 0
                        )}
                        color="amber"
                      />
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      {t('reports.yearly.trips')}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {yearlySummary.trips_count}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      {t('reports.yearly.activeDrivers')} / {t('reports.yearly.activeVehicles')}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {yearlySummary.active_drivers}
                      <span className="text-gray-400 font-normal"> / </span>
                      {yearlySummary.active_vehicles}
                    </div>
                  </div>
                </div>

                {/* Driver / Vehicle tabs */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="border-b border-gray-200">
                    <nav className="flex -mb-px">
                      <button
                        onClick={() => setActiveTab('drivers')}
                        className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                          activeTab === 'drivers'
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {t('reports.yearly.byDriver')}
                      </button>
                      <button
                        onClick={() => setActiveTab('vehicles')}
                        className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                          activeTab === 'vehicles'
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {t('reports.yearly.byVehicle')}
                      </button>
                    </nav>
                  </div>

                  {/* Drivers table */}
                  {activeTab === 'drivers' && (
                    <div className="overflow-x-auto">
                      {drivers.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 text-sm">
                          {t('reports.yearly.noData', { year: selectedYear })}
                        </div>
                      ) : (
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.driver')}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.vehicles')}
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.trips')}
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.totalKm')}
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.proKm')}
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                % Pro
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.persoKm')}
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                % Perso
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.pendingKm')}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                                Répartition
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {drivers.map((d) => (
                              <tr key={d.driver_id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                  {d.driver_name}
                                </td>
                                <td className="px-4 py-3 text-gray-600 max-w-[150px]">
                                  <span className="truncate block" title={d.vehicles.join(', ')}>
                                    {d.vehicles.join(', ') || '—'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right text-gray-700">
                                  {d.trips_count}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                                  {fmtKm(d.total_km)} km
                                </td>
                                <td className="px-4 py-3 text-right text-green-700 whitespace-nowrap">
                                  {fmtKm(d.professional_km)} km
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <PctBadge value={d.professional_pct} color="green" />
                                </td>
                                <td className="px-4 py-3 text-right text-blue-700 whitespace-nowrap">
                                  {fmtKm(d.personal_km)} km
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <PctBadge value={d.personal_pct} color="blue" />
                                </td>
                                <td className="px-4 py-3 text-right text-amber-700 whitespace-nowrap">
                                  {fmtKm(d.pending_km)} km
                                </td>
                                <td className="px-4 py-3">
                                  <KmBar
                                    proKm={d.professional_km}
                                    persoKm={d.personal_km}
                                    pendingKm={d.pending_km}
                                    totalKm={d.total_km}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* Vehicles table */}
                  {activeTab === 'vehicles' && (
                    <div className="overflow-x-auto">
                      {vehicles.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 text-sm">
                          {t('reports.yearly.noData', { year: selectedYear })}
                        </div>
                      ) : (
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.vehicle')}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.drivers')}
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.trips')}
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.totalKm')}
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.proKm')}
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                % Pro
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.persoKm')}
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                % Perso
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('reports.yearly.pendingKm')}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                                Répartition
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {vehicles.map((v) => (
                              <tr key={v.vehicle_id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="font-medium text-gray-900">{v.license_plate}</div>
                                  <div className="text-xs text-gray-500">
                                    {v.brand} {v.model}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-600 max-w-[150px]">
                                  <span className="truncate block" title={v.drivers.join(', ')}>
                                    {v.drivers.join(', ') || '—'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right text-gray-700">
                                  {v.trips_count}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                                  {fmtKm(v.total_km)} km
                                </td>
                                <td className="px-4 py-3 text-right text-green-700 whitespace-nowrap">
                                  {fmtKm(v.professional_km)} km
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <PctBadge value={v.professional_pct} color="green" />
                                </td>
                                <td className="px-4 py-3 text-right text-blue-700 whitespace-nowrap">
                                  {fmtKm(v.personal_km)} km
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <PctBadge value={v.personal_pct} color="blue" />
                                </td>
                                <td className="px-4 py-3 text-right text-amber-700 whitespace-nowrap">
                                  {fmtKm(v.pending_km)} km
                                </td>
                                <td className="px-4 py-3">
                                  <KmBar
                                    proKm={v.professional_km}
                                    persoKm={v.personal_km}
                                    pendingKm={v.pending_km}
                                    totalKm={v.total_km}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </>
        )}

      </div>
    </Layout>
  );
};
